import { useState, useMemo, useCallback } from 'react';
import {
  DataGrid,
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
} from '@tonic-one/react-data-grid';
import {
  Box,
  Button,
  LinkButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Scrollbar,
  Stack,
  Text,
  Flex,
  Spinner,
  useColorMode,
  useColorStyle,
  Collapse,
  Menu,
  MenuList,
  MenuToggle,
  MenuItem,
  Icon,
} from '@tonic-one/react';
import { getUtcTime } from '@Utils/time';
import { useTranslation } from 'react-i18next';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import TextEllipsis, {
  TextEllipsisProps,
} from '@Components/Common/Tooltip/TextEllipsis';
import { SelectionCheckboxHeader } from '../SelectionCheckboxHeader';
import {
  FeedbackMitigationEntryStatus,
  FeedbackRelatedEntry,
} from '../../type';
import { CloseSIcon, MoreIcon } from '@tonic-one/react-icons';
import IconButton from '@Components/IconButton';
import { RecommendedAction } from '../../types/remediation';
import { isMitigationActionExecutable } from '../utils';
import { LogTypeEnum } from '@Types/log';
import { generateRedirectUrlToLog } from '../../../../Log/shared';
import { useNavigateWithHandler } from '@Utils/hooks';

interface MitigationTableProps {
  data?: FeedbackRelatedEntry[];
  isLoading?: boolean;
  onExecuteActions?: (actionIds: string[]) => void;
  isAutomaticMitigation?: boolean;
  isExecuting?: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  onPaginationChange: (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  sorting: {
    columnId: string;
    direction: 'asc' | 'desc';
  };
  onSortingChange: (sorting: {
    columnId: string;
    direction: 'asc' | 'desc';
  }) => void;
  rowCount?: number;
  fetchPage?: (
    offset: number,
    limit: number
  ) => Promise<{ contentList: FeedbackRelatedEntry[]; total: number }>;
  selectableTotal?: number;
  setSelectableTotal?: (count: number | undefined) => void;
  companyGuid?: string;
  emptyContent?: string;
  height?: number;
  showPagination?: boolean;
}

export const MitigationTable = ({
  data = [],
  isLoading = false,
  onExecuteActions,
  isAutomaticMitigation = false,
  isExecuting = false,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  rowCount,
  fetchPage,
  selectableTotal,
  setSelectableTotal,
  companyGuid,
  emptyContent,
  height,
  showPagination = true,
}: MitigationTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigateWithHandler();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { offset } = useUtcOffsetStore();
  const timeFormat = t('common.momentDateFormat');

  const [rowSelection, setRowSelection] = useState<{ [key: string]: boolean }>(
    {}
  );

  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    actionIds: string[];
  }>({
    isOpen: false,
    actionIds: [],
  });
  const [allPagesAction, setAllPagesAction] =
    useState<RecommendedAction | null>(null);

  const handleSortingChange = useCallback(
    (newSorting: { columnId: string; direction: 'asc' | 'desc' }) => {
      if (
        sorting.columnId === 'message_arrive_time' &&
        newSorting.columnId === undefined &&
        newSorting.direction === undefined
      ) {
        onSortingChange({
          columnId: 'message_arrive_time',
          direction: 'asc',
        });
      } else {
        onSortingChange({
          columnId: newSorting.columnId ?? 'message_arrive_time',
          direction: newSorting.direction ?? 'desc',
        });
      }
    },
    [onSortingChange, sorting.columnId]
  );

  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      onPaginationChange?.(newPagination);
    },
    [onPaginationChange]
  );

  const RecommendedActionStrMap = useMemo(() => {
    return {
      [RecommendedAction.delete]: t('operations.euq.action.delete'),
      [RecommendedAction.quarantine]: t('operations.euq.action.quarantine'),
      [RecommendedAction.moveToJunk]: t('operations.euq.action.moveToJunk'),
      [RecommendedAction.restore]: t('operations.euq.action.restore'),
      [RecommendedAction.restoreFromQuarantine]: t(
        'operations.euq.action.restoreFromQuarantine'
      ),
      [RecommendedAction.restoreFromJunk]: t(
        'operations.euq.action.restoreFromJunk'
      ),
      [RecommendedAction.restoreFromDeletedItems]: t(
        'operations.euq.action.restoreFromDeletedItems'
      ),
    };
  }, [t]);

  const RecommendedActionCompletedStrMap = useMemo(() => {
    return {
      [RecommendedAction.delete]: t('operations.euq.action.deleteCompleted'),
      [RecommendedAction.quarantine]: t(
        'operations.euq.action.quarantineCompleted'
      ),
      [RecommendedAction.moveToJunk]: t(
        'operations.euq.action.moveToJunkCompleted'
      ),
      [RecommendedAction.restore]: t('operations.euq.action.restoreCompleted'),
      [RecommendedAction.restoreFromQuarantine]: t(
        'operations.euq.action.restoredFromQuarantine'
      ),
      [RecommendedAction.restoreFromJunk]: t(
        'operations.euq.action.restoredFromJunk'
      ),
      [RecommendedAction.restoreFromDeletedItems]: t(
        'operations.euq.action.restoredFromDeletedItems'
      ),
    };
  }, [t]);

  const extendedCheckboxColumnDef = useMemo(() => {
    return {
      ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
      header: ({ dataGrid }: any) => (
        <SelectionCheckboxHeader
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          sortedData={data}
          fetchPage={fetchPage}
          total={rowCount ?? 0}
          selectableTotal={selectableTotal}
          setSelectableTotal={setSelectableTotal}
          setAllPagesAction={setAllPagesAction}
          pagination={pagination}
          dataGrid={dataGrid}
          py="2x"
          px="3x"
        />
      ),
      headerProps: {
        p: '0',
      },
      width: 56,
      minWidth: 56,
      maxWidth: 56,
    };
  }, [
    rowSelection,
    data,
    pagination,
    fetchPage,
    rowCount,
    selectableTotal,
    setSelectableTotal,
  ]);

  const selectedActionIds = useMemo(() => {
    return Object.keys(rowSelection).filter((id) => rowSelection[id]);
  }, [rowSelection]);

  const selectedActionType = useMemo(() => {
    if (selectedActionIds.length === 0) return null;
    const firstSelectedEntry = data.find(
      (entry) => entry.id === selectedActionIds[0]
    );
    return firstSelectedEntry?.recommanded_action || allPagesAction || null;
  }, [selectedActionIds, data, allPagesAction]);

  const handleExecuteSelectedActions = useCallback(() => {
    if (selectedActionIds.length > 0 && onExecuteActions) {
      setConfirmationDialog({
        isOpen: true,
        actionIds: selectedActionIds,
      });
    }
  }, [selectedActionIds, onExecuteActions]);

  const handleSingleActionClick = useCallback((row: FeedbackRelatedEntry) => {
    setConfirmationDialog({
      isOpen: true,
      actionIds: [row.id],
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setRowSelection({});
    setAllPagesAction(null);
    setSelectableTotal?.(undefined);
  }, [setSelectableTotal]);

  const handleConfirmAction = useCallback(() => {
    if (confirmationDialog.actionIds.length > 0 && onExecuteActions) {
      onExecuteActions(confirmationDialog.actionIds);
      setConfirmationDialog({
        isOpen: false,
        actionIds: [],
      });
      setRowSelection({});
      setAllPagesAction(null);
    }
  }, [confirmationDialog.actionIds, onExecuteActions]);

  const handleCancelAction = useCallback(() => {
    setConfirmationDialog({
      isOpen: false,
      actionIds: [],
    });
  }, []);

  const handleCreateTrainingCampaign = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (emails: string[]) => {
      try {
        // const response = await V1InstanceApi.createCampaignPreparation(emails);
        // const campaignPreparationUuid = response.data.campaign_preparation_uuid;

        // // Redirect to SAA with campaign preparation UUID
        // (window as any).top?.APP?.$router.push(
        //   `/app/saa?p=training/create?cid=${campaignPreparationUuid}`
        // );
        window.open('/#/app/saa', '_blank', 'noopener, noreferrer');
        // (window as any).top?.APP?.$router.push(`/app/saa`);
      } catch (error) {
        console.error('Failed to create training campaign:', error);
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    },
    [t]
  );

  const handleViewDetails = useCallback(
    (messageId: string, action: RecommendedAction) => {
      if (!companyGuid) return;

      const isRestoredFromQuarantine =
        action === RecommendedAction.quarantine ||
        action === RecommendedAction.restoreFromQuarantine;

      const searchFields = isRestoredFromQuarantine
        ? []
        : [
            {
              field: 'malwareDetectedBy',
              values: ['Mitigation'],
              type: 'terms' as const,
            },
          ];

      const logType = isRestoredFromQuarantine
        ? LogTypeEnum.QUARANTINE
        : LogTypeEnum.DETECTION;

      const criteria = messageId
        ? [
            {
              field: isRestoredFromQuarantine
                ? 'messageId'
                : 'internetMessageId',
              value: messageId,
              label: isRestoredFromQuarantine
                ? t('log.fields.messageId')
                : t('log.fields.internetMessageId'),
            },
          ]
        : undefined;

      const urlParams = generateRedirectUrlToLog(
        searchFields,
        'all',
        logType,
        companyGuid,
        criteria
      );
      navigate(`/logs?${urlParams}`);
    },
    [companyGuid, navigate, t]
  );

  const isRowSelectable = useCallback(
    ({ row }: { row: { value: FeedbackRelatedEntry } }) => {
      return isMitigationActionExecutable(row.value);
    },
    []
  );

  const columns = useMemo(() => {
    const Ellipsis = ({ children }: TextEllipsisProps) => (
      <TextEllipsis popperProps={{ usePortal: true }}>{children}</TextEllipsis>
    );

    const baseColumns = [
      {
        id: 'message_arrive_time',
        accessorKey: 'message_arrive_time',
        header: t('operations.euq.detail.table.header.arrivingTime'),
        cell: ({ row }: { row: { value: FeedbackRelatedEntry } }) => (
          <Ellipsis>
            {getUtcTime(row.value.message_arrive_time, offset, timeFormat)}
          </Ellipsis>
        ),
        minWidth: 230,
        width: 'auto',
      },
      {
        id: 'message_subject',
        accessorKey: 'message_subject',
        header: t('operations.euq.detail.table.header.subject'),
        cell: ({ row }: { row: { value: FeedbackRelatedEntry } }) => (
          <Ellipsis fontWeight={500}>{row.value.message_subject}</Ellipsis>
        ),
        minWidth: 270,
        width: 'auto',
      },
      {
        id: 'message_sender',
        accessorKey: 'message_sender',
        header: t('operations.euq.detail.table.header.sender'),
        cell: ({ row }: { row: { value: FeedbackRelatedEntry } }) => (
          <Ellipsis>{row.value.message_sender}</Ellipsis>
        ),
        minWidth: 270,
        width: 'auto',
      },
      {
        id: 'message_recipients',
        accessorKey: 'message_recipients',
        header: t('operations.euq.detail.table.header.recipient'),
        cell: ({ row }: { row: { value: FeedbackRelatedEntry } }) => (
          <Ellipsis>{row.value.message_recipients}</Ellipsis>
        ),
        minWidth: 270,
        width: 'auto',
      },
      {
        id: 'message_violators',
        accessorKey: 'message_violators',
        header: t('operations.euq.table.affectedUsers'),
        cell: ({ row }: { row: { value: FeedbackRelatedEntry } }) => {
          const violators = row.value.message_violators;
          const emails =
            typeof violators === 'string'
              ? violators.split(';')
              : violators || [];
          return (
            <Flex flex={1} justifyContent="end" alignItems="center">
              {emails.length === 0 ? (
                <Text>0</Text>
              ) : (
                <Popover placement="right">
                  <PopoverTrigger>
                    <LinkButton>{emails.length}</LinkButton>
                  </PopoverTrigger>
                  <PopoverContent
                    maxWidth={350}
                    PopperProps={{ usePortal: true }}
                  >
                    <PopoverBody>
                      <Scrollbar overflow="scroll" maxHeight={200} p="1x">
                        <Stack as="ul" gap="2x" m="0" pl="4x">
                          {emails.map((email: string, index: number) => (
                            <Box as="li" key={index}>
                              <Text display="inline">{email}</Text>
                            </Box>
                          ))}
                        </Stack>
                      </Scrollbar>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )}
            </Flex>
          );
        },
        headerProps: {
          justifyContent: 'flex-end',
        },
        cellProps: {
          textAlign: 'end',
        },
        minWidth: 170,
        width: 'auto',
      },
      {
        id: 'recommanded_action',
        accessorKey: 'recommanded_action',
        header: t('operations.euq.table.result'),
        cell: ({ row }: { row: { value: FeedbackRelatedEntry } }) => {
          if (row.value.status === FeedbackMitigationEntryStatus.DONE) {
            return (
              <Flex alignItems="center" gap="2x">
                <Text>
                  {
                    RecommendedActionCompletedStrMap[
                      row.value.recommanded_action
                    ]
                  }
                </Text>
                <LinkButton
                  onClick={() =>
                    handleViewDetails(
                      row.value.message_id,
                      row.value.recommanded_action
                    )
                  }
                >
                  {t('administration.sa.table.status.btn.details')}
                </LinkButton>
              </Flex>
            );
          } else if (
            row.value.status === FeedbackMitigationEntryStatus.IN_PROGRESS
          ) {
            return (
              <Ellipsis>
                {isAutomaticMitigation
                  ? t('operations.euq.action.autoMitigationInProgress')
                  : t('operations.euq.action.mitigationInProgress')}
              </Ellipsis>
            );
          }
          return (
            <LinkButton
              onClick={() => handleSingleActionClick(row.value)}
              textAlign="left"
              width="100%"
            >
              <Ellipsis>
                {RecommendedActionStrMap[row.value.recommanded_action]}
              </Ellipsis>
            </LinkButton>
          );
        },
        minWidth: 270,
        width: 'auto',
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: { row: { value: FeedbackRelatedEntry } }) => {
          const violators = row.value.message_violators;
          const emails =
            typeof violators === 'string'
              ? violators.split(';')
              : violators || [];

          if (emails.length === 0) {
            return null;
          }

          return (
            <Menu placement="bottom-end">
              <MenuToggle>
                <Icon as={MoreIcon} cursor="pointer" />
              </MenuToggle>
              <MenuList
                PopperProps={{
                  modifiers: [
                    {
                      name: 'flip',
                    },
                  ],
                  usePortal: true,
                }}
              >
                <MenuItem onClick={() => handleCreateTrainingCampaign(emails)}>
                  {t('operations.euq.action.createTraining')}
                </MenuItem>
              </MenuList>
            </Menu>
          );
        },
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        isSortable: false,
        isResizable: false,
      },
    ];

    // Return columns with checkbox column only if not automatic mitigation
    return isAutomaticMitigation
      ? baseColumns
      : [extendedCheckboxColumnDef, ...baseColumns];
  }, [
    extendedCheckboxColumnDef,
    t,
    isAutomaticMitigation,
    offset,
    timeFormat,
    RecommendedActionStrMap,
    RecommendedActionCompletedStrMap,
    handleViewDetails,
    handleSingleActionClick,
    handleCreateTrainingCampaign,
  ]);

  return (
    <Stack flex={1}>
      <Collapse
        in={
          !isAutomaticMitigation &&
          onExecuteActions &&
          selectedActionIds.length > 0
        }
      >
        <Flex alignItems="center" columnGap="3x" px="4x" py="2x">
          <Button
            variant="secondary"
            onClick={handleExecuteSelectedActions}
            disabled={isExecuting}
            columnGap="2x"
          >
            {selectedActionType && RecommendedActionStrMap[selectedActionType]}
            {isExecuting && <Spinner size="xs" lineColor="inherit" />}
          </Button>
          <Flex columnGap="1x" alignItems="center">
            <IconButton onClick={handleClearSelection}>
              <CloseSIcon size="4x" />
            </IconButton>
            <Text color={colorStyle.color.secondary}>
              {Object.entries(rowSelection).length === rowCount ? (
                t('operations.euq.action.allSelectedCurrentPage')
              ) : (
                <>
                  {Object.entries(rowSelection).length}{' '}
                  {t('common.label.selected')}
                </>
              )}
            </Text>
          </Flex>
        </Flex>
      </Collapse>

      <Flex height={height ?? 400}>
        <DataGrid
          data={data}
          columns={columns}
          rowSelection={isAutomaticMitigation ? {} : rowSelection}
          onRowSelectionChange={
            isAutomaticMitigation ? undefined : setRowSelection
          }
          isRowSelectable={isAutomaticMitigation ? undefined : isRowSelectable}
          isColumnSortable={true}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          isColumnResizable={true}
          showPagination={showPagination}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
          emptyContent={emptyContent ?? t('common.noData')}
          isLoading={isLoading}
          disableAutoResetRowSelection
          disableAutoResetPageIndex
          slotProps={{
            pagination: {
              background: colorStyle.background.primary,
              borderTop: '1px solid',
              borderTopColor: colorStyle.divider,
            },
          }}
        />
      </Flex>

      {/* Confirmation Dialog */}
      {confirmationDialog.isOpen && (
        <Modal
          closeOnEsc
          closeOnOutsideClick
          isClosable
          isOpen={confirmationDialog.isOpen}
          onClose={handleCancelAction}
          size="sm"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('operations.euq.confirmation.action')}</ModalHeader>
            <ModalBody>
              <Text>
                {confirmationDialog.actionIds.length > 1
                  ? t('operations.euq.confirmation.executeBulk', {
                      selectedItems: confirmationDialog.actionIds.length,
                    })
                  : t('operations.euq.confirmation.executeAction')}
              </Text>
            </ModalBody>
            <ModalFooter columnGap="2x">
              <Button minWidth="20x" onClick={handleCancelAction}>
                {t('common.button.cancel')}
              </Button>
              <Button
                minWidth="20x"
                variant="primary"
                onClick={handleConfirmAction}
                disabled={isExecuting}
              >
                {t('common.button.apply')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Stack>
  );
};
